import * as React from 'react';
import {Box, Paper, Typography, IconButton, Grid, Stack, Card} from '@mui/material';
import {
  ArrowBackIos,
  ArrowForwardIos,
  CalendarMonthOutlined,
  CalendarToday,
} from '@mui/icons-material';
import {LocalizationProvider, DatePicker} from '@mui/x-date-pickers';
import {AdapterDayjs} from '@mui/x-date-pickers/AdapterDayjs';
import ZeiterfassungContext, { IArbeitszeitenDay } from '../../../../Contexts/ZeiterfassungContext/ZeiterfassungContext';
import dayjs from 'dayjs';
import 'dayjs/locale/de';
import UserCalendar, {CalendarButton} from './UserCalendar';

interface IZeiterfassungSelectDatum {}

export default function ZeiterfassungSelectDatum(props: IZeiterfassungSelectDatum) {
  const {selectedDate, setSelectedDate, arbeitszeitenDay} = React.useContext(ZeiterfassungContext);

  const handleDateChange = (date: Date | null) => {
    if (date) {
      setSelectedDate(date);
    }
  };

  const goToPreviousDay = () => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() - 1);
    setSelectedDate(newDate);
  };

  const goToNextDay = () => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() + 1);
    setSelectedDate(newDate);
  };

  const goToToday = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    setSelectedDate(today);
  };

  // Format day name and date
  const formattedDayName = dayjs(selectedDate).locale('de').format('dddd');
  const formattedDate = dayjs(selectedDate).format('DD.MM.YYYY');
  const isToday = dayjs(selectedDate).isSame(dayjs().startOf('day'), 'day');

  const formatDuration = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds - hours * 3600) / 60);
    return `${hours > 9 ? '' : '0'}${hours}:${minutes > 9 ? '' : '0'}${minutes}`;
  };

  // Calculate total work time for the day
  const totalWorkTime = arbeitszeitenDay.reduce(
    (total: number, entry: IArbeitszeitenDay) =>
      total + ((entry.ende.getTime() - entry.start.getTime()) / 1000 - entry.pause),
    0
  );

  return (
    <Card sx={{p: 2, mb: 2}}>
      <Grid container alignItems='center' spacing={2}>
        <Grid item xs={12} sm={6}>
          <Stack direction='row' alignItems='center' spacing={1}>
            <Box>
              <Typography variant='body2' color='text.secondary' sx={{textTransform: 'capitalize'}}>
                {formattedDayName}
              </Typography>
              <Typography variant='h6' sx={{fontWeight: 'bold'}}>
                {formattedDate}
                {isToday && (
                  <Typography
                    component='span'
                    variant='caption'
                    sx={{
                      ml: 1,
                      bgcolor: 'primary.main',
                      color: 'white',
                      px: 1,
                      py: 0.5,
                      borderRadius: 1,
                      fontWeight: 'bold',
                    }}>
                    Heute
                  </Typography>
                )}
              </Typography>
            </Box>
          </Stack>
        </Grid>

        <Grid item xs={12} sm={6}>
          <Stack
            direction='row'
            spacing={1}
            justifyContent={{xs: 'flex-start', sm: 'flex-end'}}
            alignItems='center'
            position='relative'>
            {!isToday && (
              <Typography
                variant='body2'
                color='primary'
                sx={{
                  cursor: 'pointer',
                  fontWeight: 'medium',
                  '&:hover': {textDecoration: 'underline'},
                  position: {xs: 'absolute', sm: 'static'},
                  right: {xs: 0, sm: 'auto'},
                  top: {xs: '-30px', sm: 'auto'},
                  display: 'flex',
                  alignItems: 'center',
                }}
                onClick={goToToday}>
                <CalendarToday fontSize='small' sx={{mr: 0.5, fontSize: '0.9rem'}} />
                Heute
              </Typography>
            )}

            <IconButton
              onClick={goToPreviousDay}
              sx={{
                borderRadius: 1,
                bgcolor: 'background.paper',
                border: '1px solid rgba(0, 0, 0, 0.08)',
                '&:hover': {
                  bgcolor: 'rgba(25, 118, 210, 0.08)',
                },
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 32,
                height: 32,
                p: 0,
              }}
              size='small'>
              <ArrowBackIos
                fontSize='small'
                sx={{
                  fontSize: '1rem',
                  ml: 0.5, // Add margin-left to center the icon visually
                }}
              />
            </IconButton>

            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale='de'>
              <DatePicker
                value={dayjs(selectedDate)}
                onChange={(value) => value && handleDateChange(value.toDate())}
                slotProps={{
                  textField: {
                    size: 'small',
                    fullWidth: true,
                    sx: {
                      width: {xs: '100%', sm: 'auto'},
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 1,
                      },
                    },
                  },
                }}
              />
            </LocalizationProvider>

            <IconButton
              onClick={goToNextDay}
              sx={{
                borderRadius: 1,
                bgcolor: 'background.paper',
                border: '1px solid rgba(0, 0, 0, 0.08)',
                '&:hover': {
                  bgcolor: 'rgba(25, 118, 210, 0.08)',
                },
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 32,
                height: 32,
                p: 0,
              }}
              size='small'>
              <ArrowForwardIos fontSize='small' sx={{fontSize: '1rem'}} />
            </IconButton>
          </Stack>
        </Grid>
      </Grid>
  
    </Card>
  );
}
